/**
 * CELESTIAL BODIES — the cartoon sun and moon on the Canvas 2D path, and the pose (position,
 * squash and stretch, ray spin, sunset look) that both renderers share.
 *
 * The pictures are baked once in render/toon/sky-art.js; this module only places them. The
 * pose is built from the hour-driven orbit plus the pause-aware wobble clock, so the body
 * wobbles at a steady pace through the whole cycle, the horizon included.
 */

import { normalizeTimeOfDay, lerp } from "./sky-utils.js";
import {
  celestialSquash,
  sunRayAngle,
  sunRayPulse,
  sunsetAmount,
  moonDarkRgb,
  drawCelestialPose,
  mixRgb,
  CELESTIAL_MAX_STRETCH,
  SUN_HALO_RGB,
  SUN_SUNSET_HALO_RGB,
  MOON_HALO_RGB,
} from "../toon/sky-art.js";

/**
 * Sun opacity across the day for the legacy normalized-phase path (kept for the uniform
 * contract). The cartoon sun is opaque whenever it is up; the sea clips it at the horizon.
 */
export function sunAlpha(timeOfDay) {
  var t = normalizeTimeOfDay(timeOfDay);
  if (t > 0.62 && t < 0.88) return 0;
  if (t <= 0.12) return lerp(0.35, 0.95, t / 0.12);
  if (t < 0.25) return lerp(0.95, 1, (t - 0.12) / 0.13);
  if (t <= 0.38) return 1;
  if (t < 0.5) return lerp(1, 0.55, (t - 0.38) / 0.12);
  if (t <= 0.62) return lerp(0.55, 0, (t - 0.5) / 0.12);
  if (t >= 0.88) return lerp(0, 0.4, (t - 0.88) / 0.12);
  return 0;
}

/**
 * True when any part of the (possibly stretched) body pokes above the sea line. The core
 * visibility gate uses the plain radius; a wobble-stretched top must not appear before it.
 */
export function isCelestialPoseVisible(y, radius, seaLineY) {
  return (y - radius * CELESTIAL_MAX_STRETCH) < seaLineY;
}

/**
 * The pose of the active body for one frame.
 * @param {{ isDay: boolean, x: number, y: number, r: number, seaLineY: number,
 *   colorPhase: number, wobbleSec?: number, skyRgb?: number[] }} o
 * @returns {{ kind: "sun"|"moon", x: number, y: number, r: number, sx: number, sy: number,
 *   rayRot: number, rayPulse: number, sunset: number, haloRgb: number[], haloAlpha: number,
 *   darkRgb: number[]|null, visible: boolean }}
 */
export function celestialPose(o) {
  var kind = o.isDay ? "sun" : "moon";
  var r = Math.max(0, Number(o.r) || 0);
  var wobble = Number(o.wobbleSec) || 0;
  var sq = celestialSquash(kind, wobble);
  var sunset = kind === "sun" ? sunsetAmount(o.colorPhase) : 0;
  return {
    kind: kind,
    x: o.x,
    y: o.y,
    r: r,
    sx: sq.sx,
    sy: sq.sy,
    rayRot: kind === "sun" ? sunRayAngle(wobble) : 0,
    rayPulse: kind === "sun" ? sunRayPulse(wobble) : 1,
    sunset: sunset,
    haloRgb: kind === "sun" ? mixRgb(SUN_HALO_RGB, SUN_SUNSET_HALO_RGB, sunset) : MOON_HALO_RGB,
    haloAlpha: 1,
    darkRgb: kind === "moon" ? moonDarkRgb(o.skyRgb) : null,
    visible: r > 0 && isFinite(o.x) && isFinite(o.y) && isCelestialPoseVisible(o.y, r, o.seaLineY),
  };
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} x
 * @param {number} y
 * @param {number} radius
 * @param {number} timeOfDay colour phase
 * @param {{ wobbleSec?: number, seaLineY?: number, dpr?: number }} [opts]
 */
export function drawSun(ctx, x, y, radius, timeOfDay, opts) {
  if (!ctx || !isFinite(x) || !isFinite(y)) return;
  var r = Math.max(0, Number(radius) || 0);
  if (r <= 0) return;
  opts = opts || {};
  var pose = celestialPose({
    isDay: true,
    x: x,
    y: y,
    r: r,
    seaLineY: opts.seaLineY != null ? opts.seaLineY : Infinity,
    colorPhase: timeOfDay,
    wobbleSec: opts.wobbleSec,
  });
  drawCelestialPose(ctx, pose, opts.dpr || 1);
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} x
 * @param {number} y
 * @param {number} radius
 * @param {{ skyColor?: number[], wobbleSec?: number, seaLineY?: number, dpr?: number }} [opts]
 */
export function drawMoon(ctx, x, y, radius, opts) {
  if (!ctx || !isFinite(x) || !isFinite(y)) return;
  var r = Math.max(0, Number(radius) || 0);
  if (r <= 0) return;
  opts = opts || {};
  var pose = celestialPose({
    isDay: false,
    x: x,
    y: y,
    r: r,
    seaLineY: opts.seaLineY != null ? opts.seaLineY : Infinity,
    colorPhase: 0.75,
    wobbleSec: opts.wobbleSec,
    skyRgb: opts.skyColor,
  });
  drawCelestialPose(ctx, pose, opts.dpr || 1);
}
